import { __iconNode as briefcaseBusinessNode } from 'lucide-react/dist/esm/icons/briefcase-business.mjs';
import { __iconNode as buildingNode } from 'lucide-react/dist/esm/icons/building.mjs';
import { __iconNode as building2Node } from 'lucide-react/dist/esm/icons/building-2.mjs';
import { __iconNode as carNode } from 'lucide-react/dist/esm/icons/car.mjs';
import { __iconNode as churchNode } from 'lucide-react/dist/esm/icons/church.mjs';
import { __iconNode as circleDollarSignNode } from 'lucide-react/dist/esm/icons/circle-dollar-sign.mjs';
import { __iconNode as coffeeNode } from 'lucide-react/dist/esm/icons/coffee.mjs';
import { __iconNode as dumbbellNode } from 'lucide-react/dist/esm/icons/dumbbell.mjs';
import { __iconNode as filmNode } from 'lucide-react/dist/esm/icons/film.mjs';
import { __iconNode as graduationCapNode } from 'lucide-react/dist/esm/icons/graduation-cap.mjs';
import { __iconNode as heartPulseNode } from 'lucide-react/dist/esm/icons/heart-pulse.mjs';
import { __iconNode as hotelNode } from 'lucide-react/dist/esm/icons/hotel.mjs';
import { __iconNode as landmarkNode } from 'lucide-react/dist/esm/icons/landmark.mjs';
import { __iconNode as mapNode } from 'lucide-react/dist/esm/icons/map.mjs';
import { __iconNode as martiniNode } from 'lucide-react/dist/esm/icons/martini.mjs';
import { __iconNode as paletteNode } from 'lucide-react/dist/esm/icons/palette.mjs';
import { __iconNode as shoppingBagNode } from 'lucide-react/dist/esm/icons/shopping-bag.mjs';
import { __iconNode as shoppingCartNode } from 'lucide-react/dist/esm/icons/shopping-cart.mjs';
import { __iconNode as trainFrontNode } from 'lucide-react/dist/esm/icons/train-front.mjs';
import { __iconNode as treesNode } from 'lucide-react/dist/esm/icons/trees.mjs';
import { __iconNode as utensilsNode } from 'lucide-react/dist/esm/icons/utensils.mjs';
import { __iconNode as wrenchNode } from 'lucide-react/dist/esm/icons/wrench.mjs';
import type { IconNode } from 'lucide-react';
import { lucideIconNodeToSvg } from '@/src/lib/utils/lucideToSvg';
import { resolveTokenValue } from '@/src/lib/utils/resolveTokenValue';
import type { POIIconGroup, POIOptionValue } from './types';
import { getPOISearchConfig } from './types';

const POI_ICON_NODES: Record<POIIconGroup, IconNode> = {
  restaurants: utensilsNode,
  cafes: coffeeNode,
  groceries: shoppingCartNode,
  shopping: shoppingBagNode,
  nightlife: martiniNode,
  automotive: carNode,
  business: briefcaseBusinessNode,
  culture: paletteNode,
  education: graduationCapNode,
  entertainment: filmNode,
  facilities: buildingNode,
  finance: circleDollarSignNode,
  foodDrink: utensilsNode,
  geographicalAreas: mapNode,
  government: landmarkNode,
  healthWellness: heartPulseNode,
  housing: building2Node,
  lodging: hotelNode,
  naturalFeatures: treesNode,
  worship: churchNode,
  services: wrenchNode,
  sportsFitness: dumbbellNode,
  transportation: trainFrontNode,
};

const MARKER_SIZE = 24;
const ICON_INNER_SIZE = 16;
const poiDataUrls = new Map<POIIconGroup, string>();

function getDataUrl(optionValue: POIOptionValue): string {
  const config = getPOISearchConfig(optionValue);
  const cached = poiDataUrls.get(config.iconGroup);
  if (cached) return cached;

  const color = resolveTokenValue(config.color);
  const iconInner = lucideIconNodeToSvg(POI_ICON_NODES[config.iconGroup]);
  const center = MARKER_SIZE / 2;
  const radius = center - 1;
  const iconOffset = (MARKER_SIZE - ICON_INNER_SIZE) / 2;
  const iconScale = ICON_INNER_SIZE / 24;
  const strokeCompensation = 2 / iconScale;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${MARKER_SIZE}" height="${MARKER_SIZE}" viewBox="0 0 ${MARKER_SIZE} ${MARKER_SIZE}">
    <circle cx="${center}" cy="${center}" r="${radius}" fill="${color}" stroke="white" stroke-width="1"/>
    <g transform="translate(${iconOffset}, ${iconOffset}) scale(${iconScale})" fill="none" stroke="white" stroke-width="${strokeCompensation}" stroke-linecap="round" stroke-linejoin="round">${iconInner}</g>
  </svg>`;

  const url = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  poiDataUrls.set(config.iconGroup, url);
  return url;
}

export function createPOIMarkerIcon(optionValue: POIOptionValue): google.maps.Icon {
  return {
    url: getDataUrl(optionValue),
    scaledSize: new google.maps.Size(MARKER_SIZE, MARKER_SIZE),
    anchor: new google.maps.Point(MARKER_SIZE / 2, MARKER_SIZE / 2),
  };
}
